'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetGiftsField } from '@morfi/backend-sdk-v2/client';
import { CompactGiftRow, type CompactGiftRowVariant } from './compact-gift-row';
import type { GiftRowData, GiftRowStatus } from './gift-row';
interface LiveCompactGiftRowProps {
  /**
   * SDK rowKey for this gift — the value of the `giftPda` field (the
   * `useGetGifts` spec's `rowKey`). `<Items>` provides this as the `id`
   * in its render callback.
   */
  id: string;
  variant: CompactGiftRowVariant;
  /**
   * Stable-field snapshot from `query.rows`. Used for amount, sender,
   * message, createdAt, asset-kind, etc. — fields that never change
   * after a gift is created. Volatile `status` is replaced below with
   * the live cache value.
   */
  snapshot: GiftRowData;
}

/**
 * Subscribes to the gift's `status` field directly from the SDK cache
 * via `useGetGiftsField` so NATS / mutation-optimistic patches
 * (gift claimed, gift returned, gift withdrawn) flip the row UI without
 * re-rendering the parent feed. Stable fields ride on `snapshot`.
 *
 * Why this exists: the page-level observer on `useGetGiftsInfinite` is
 * silent on `data` changes by design (see `createListHook` —
 * `PAGE_LEVEL_NOTIFY_ON_CHANGE_PROPS` excludes `data`). Reading row
 * fields off a prop spread from `query.rows` therefore yields the
 * first-paint snapshot forever. The fix is to subscribe per-row.
 * Mirrors the proposal-feed `<ProposalRowLeaf>` and
 * `SuggestionFeedRow` patterns. Lives in a shared file because both
 * `<AllGiftsDialog>` and `<GiftsSummaryCard>` render the same compact
 * gift row.
 */
export function LiveCompactGiftRow(t0) {
  const $ = _c(6);
  const {
    id,
    variant,
    snapshot
  } = t0;
  const liveStatus = useGetGiftsField(id, "status");
  const t1 = (liveStatus ?? snapshot.status) as GiftRowStatus;
  let t2;
  if ($[0] !== snapshot || $[1] !== t1) {
    t2 = {
      ...snapshot,
      status: t1
    };
    $[0] = snapshot;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const data = t2;
  let t3;
  if ($[3] !== data || $[4] !== variant) {
    t3 = <CompactGiftRow variant={variant} data={data} />;
    $[3] = data;
    $[4] = variant;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}